import { useState, useEffect } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Button } from '@/components/ui/button'
import { Slider } from '@/components/ui/slider'
import { Switch } from '@/components/ui/switch'
import { Badge } from '@/components/ui/badge'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Checkbox } from '@/components/ui/checkbox'
import { 
  Scale, 
  Info, 
  TrendingDown, 
  AlertTriangle, 
  Target,
  Calculator,
  Save
} from 'lucide-react'
import type { 
  MaterialityThresholds, 
  MaterialityThreshold, 
  MaterialityBasis, 
  SpecificMaterialityArea 
} from '@/lib/modules/types'

interface MaterialityThresholdsPanelProps {
  thresholds: MaterialityThresholds;
  onThresholdsChange: (thresholds: MaterialityThresholds) => void;
  onSave?: () => Promise<void>;
  isSaving?: boolean;
}

const THRESHOLD_DEFINITIONS = {
  overall_materiality: {
    title: 'Väsentlighet (Overall Materiality)',
    description: 'Övergripande gränsen för fel eller utelämnanden som kan påverka användarens beslut baserat på årsredovisningen.',
    usage: 'Används för slutomdöme och som referensram för alla andra trösklar.',
    icon: Scale,
    color: 'text-primary'
  },
  performance_materiality: {
    title: 'Prestandaväsentlighet (Performance Materiality)',
    description: 'En lägre nivå för att minska risken att summan av icke-korrigerade och oupptäckta fel överstiger väsentlighet.',
    usage: 'Styr omfattning och djup i granskningsåtgärder.',
    icon: Target,
    color: 'text-warning'
  },
  clearly_trivial_threshold: {
    title: 'Trivialitetsgräns (Clearly Trivial)',
    description: 'Gräns under vilken fel bedöms vara uppenbart betydelselösa.',
    usage: 'Filtrerar bort oväsentliga avvikelser från analyser och rapporter.',
    icon: TrendingDown,
    color: 'text-muted-foreground'
  },
  specific_materiality: {
    title: 'Specifik väsentlighet',
    description: 'Särskilt låg tröskel för områden där även små belopp kan vara väsentliga på grund av sin natur.',
    usage: 'Närståendetransaktioner, ledningsersättningar, legal compliance.',
    icon: AlertTriangle,
    color: 'text-destructive'
  }
};

const BASIS_OPTIONS: { value: MaterialityBasis; label: string }[] = [
  { value: 'result_before_tax', label: 'Resultat före skatt' },
  { value: 'revenue', label: 'Omsättning' },
  { value: 'equity', label: 'Eget kapital' },
  { value: 'total_assets', label: 'Totala tillgångar' },
  { value: 'custom', label: 'Manuellt belopp' }
];

const SPECIFIC_AREAS: { value: SpecificMaterialityArea; label: string }[] = [
  { value: 'related_parties', label: 'Närståendetransaktioner' },
  { value: 'management_compensation', label: 'Ledningsersättningar' },
  { value: 'legal_compliance', label: 'Legal compliance' },
  { value: 'regulatory_matters', label: 'Regulatoriska frågor' },
  { value: 'fraud_risk', label: 'Bedrägeririsk' }
];

function formatCurrency(value: number): string {
  if (value >= 1000000) {
    return `${(value / 1000000).toFixed(1)} mkr`;
  } else if (value >= 1000) {
    return `${Math.round(value / 1000)} tkr`;
  }
  return `${Math.round(value)} kr`;
}

function ThresholdInfoTooltip({ type }: { type: keyof typeof THRESHOLD_DEFINITIONS }) {
  const def = THRESHOLD_DEFINITIONS[type];
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <Info className="h-4 w-4 text-muted-foreground cursor-help" />
        </TooltipTrigger>
        <TooltipContent className="max-w-xs">
          <p className="font-medium">{def.title}</p>
          <p className="text-sm text-muted-foreground mt-1">{def.description}</p>
          <p className="text-xs text-muted-foreground mt-2 italic">{def.usage}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

export function MaterialityThresholdsPanel({ 
  thresholds, 
  onThresholdsChange,
  onSave,
  isSaving 
}: MaterialityThresholdsPanelProps) {
  // Helper to update a specific threshold
  const updateThreshold = (
    key: keyof MaterialityThresholds, 
    updates: Partial<MaterialityThreshold>
  ) => {
    const current = thresholds[key] || { value: 0 };
    onThresholdsChange({
      ...thresholds,
      [key]: { ...current, ...updates }
    });
  };

  // Calculate derived values
  const overallValue = thresholds.overall_materiality?.value || 0;
  const performancePercentage = thresholds.performance_materiality?.percentage || 60;
  const trivialPercentage = thresholds.clearly_trivial_threshold?.percentage || 5;

  const calculatedPerformance = Math.round(overallValue * (performancePercentage / 100));
  const calculatedTrivial = Math.round(overallValue * (trivialPercentage / 100));

  // Auto-update derived values when overall changes
  useEffect(() => {
    if (overallValue > 0) {
      if (thresholds.performance_materiality?.derived_from === 'overall_materiality') {
        updateThreshold('performance_materiality', { value: calculatedPerformance });
      }
      if (thresholds.clearly_trivial_threshold?.derived_from === 'overall_materiality') {
        updateThreshold('clearly_trivial_threshold', { value: calculatedTrivial });
      }
    }
  }, [overallValue, performancePercentage, trivialPercentage]);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Scale className="w-5 h-5" />
          Revisionströsklar
        </CardTitle>
        <CardDescription>
          Konfigurera väsentlighetströsklar för analyser och bedömningar. Dessa trösklar används konsekvent i alla AI-analyser.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <Accordion type="multiple" defaultValue={['overall', 'performance', 'trivial']} className="space-y-4">
          {/* Overall Materiality */}
          <AccordionItem value="overall" className="border rounded-lg px-4">
            <AccordionTrigger className="hover:no-underline">
              <div className="flex items-center gap-3">
                <Scale className="h-5 w-5 text-primary" />
                <span className="font-medium">Väsentlighet (Overall Materiality)</span>
                {overallValue > 0 && (
                  <Badge variant="secondary">{formatCurrency(overallValue)}</Badge>
                )}
              </div>
            </AccordionTrigger>
            <AccordionContent className="pt-4 space-y-4">
              <div className="flex items-start gap-2 text-sm text-muted-foreground bg-muted/50 p-3 rounded-md">
                <Info className="h-4 w-4 mt-0.5 shrink-0" />
                <p>{THRESHOLD_DEFINITIONS.overall_materiality.description}</p>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    Beräkningsbas
                    <ThresholdInfoTooltip type="overall_materiality" />
                  </Label>
                  <Select
                    value={thresholds.overall_materiality?.basis || 'result_before_tax'}
                    onValueChange={(value) => updateThreshold('overall_materiality', { 
                      basis: value as MaterialityBasis 
                    })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {BASIS_OPTIONS.map(opt => (
                        <SelectItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label>Belopp (SEK)</Label>
                  <Input
                    type="number"
                    placeholder="2000000"
                    value={thresholds.overall_materiality?.value || ''}
                    onChange={(e) => updateThreshold('overall_materiality', { 
                      value: parseInt(e.target.value) || 0 
                    })}
                    className="font-mono"
                  />
                </div>
              </div>
              
              {thresholds.overall_materiality?.basis !== 'custom' && (
                <div className="space-y-2">
                  <Label>Procent av bas</Label>
                  <div className="flex items-center gap-4">
                    <Slider
                      value={[thresholds.overall_materiality?.percentage || 5]}
                      onValueChange={([value]) => updateThreshold('overall_materiality', { 
                        percentage: value 
                      })}
                      min={1}
                      max={10}
                      step={0.5}
                      className="flex-1"
                    />
                    <span className="text-sm font-medium w-12 text-right">
                      {thresholds.overall_materiality?.percentage || 5}%
                    </span>
                  </div>
                </div>
              )}
            </AccordionContent>
          </AccordionItem>

          {/* Performance Materiality */}
          <AccordionItem value="performance" className="border rounded-lg px-4">
            <AccordionTrigger className="hover:no-underline">
              <div className="flex items-center gap-3">
                <Target className="h-5 w-5 text-warning" />
                <span className="font-medium">Prestandaväsentlighet</span>
                {(thresholds.performance_materiality?.value || calculatedPerformance) > 0 && (
                  <Badge variant="secondary">
                    {formatCurrency(thresholds.performance_materiality?.value || calculatedPerformance)}
                  </Badge>
                )}
              </div>
            </AccordionTrigger>
            <AccordionContent className="pt-4 space-y-4">
              <div className="flex items-start gap-2 text-sm text-muted-foreground bg-muted/50 p-3 rounded-md">
                <Info className="h-4 w-4 mt-0.5 shrink-0" />
                <p>{THRESHOLD_DEFINITIONS.performance_materiality.description}</p>
              </div>
              
              <div className="flex items-center gap-4">
                <Switch
                  checked={thresholds.performance_materiality?.derived_from === 'overall_materiality'}
                  onCheckedChange={(checked) => updateThreshold('performance_materiality', { 
                    derived_from: checked ? 'overall_materiality' : undefined,
                    value: checked ? calculatedPerformance : thresholds.performance_materiality?.value || 0
                  })}
                />
                <Label className="cursor-pointer">Beräkna från väsentlighet</Label>
              </div>
              
              {thresholds.performance_materiality?.derived_from === 'overall_materiality' ? (
                <div className="space-y-2">
                  <Label>Procent av väsentlighet</Label>
                  <div className="flex items-center gap-4">
                    <Slider
                      value={[performancePercentage]}
                      onValueChange={([value]) => updateThreshold('performance_materiality', { 
                        percentage: value,
                        value: Math.round(overallValue * (value / 100))
                      })}
                      min={50}
                      max={85}
                      step={5}
                      className="flex-1"
                    />
                    <span className="text-sm font-medium w-12 text-right">{performancePercentage}%</span>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    = {formatCurrency(calculatedPerformance)} (vanligt intervall: 50–85% av väsentlighet)
                  </p>
                </div>
              ) : (
                <div className="space-y-2">
                  <Label>Belopp (SEK)</Label>
                  <Input
                    type="number"
                    placeholder="1200000"
                    value={thresholds.performance_materiality?.value || ''}
                    onChange={(e) => updateThreshold('performance_materiality', { 
                      value: parseInt(e.target.value) || 0 
                    })}
                    className="font-mono"
                  />
                </div>
              )}
            </AccordionContent>
          </AccordionItem>

          {/* Clearly Trivial Threshold */}
          <AccordionItem value="trivial" className="border rounded-lg px-4">
            <AccordionTrigger className="hover:no-underline">
              <div className="flex items-center gap-3">
                <TrendingDown className="h-5 w-5 text-muted-foreground" />
                <span className="font-medium">Trivialitetsgräns</span>
                {(thresholds.clearly_trivial_threshold?.value || calculatedTrivial) > 0 && (
                  <Badge variant="outline">
                    {formatCurrency(thresholds.clearly_trivial_threshold?.value || calculatedTrivial)}
                  </Badge>
                )}
              </div>
            </AccordionTrigger>
            <AccordionContent className="pt-4 space-y-4">
              <div className="flex items-start gap-2 text-sm text-muted-foreground bg-muted/50 p-3 rounded-md">
                <Info className="h-4 w-4 mt-0.5 shrink-0" />
                <p>{THRESHOLD_DEFINITIONS.clearly_trivial_threshold.description}</p>
              </div>
              
              <div className="flex items-center gap-4">
                <Switch
                  checked={thresholds.clearly_trivial_threshold?.derived_from === 'overall_materiality'}
                  onCheckedChange={(checked) => updateThreshold('clearly_trivial_threshold', { 
                    derived_from: checked ? 'overall_materiality' : undefined,
                    value: checked ? calculatedTrivial : thresholds.clearly_trivial_threshold?.value || 0
                  })}
                />
                <Label className="cursor-pointer">Beräkna från väsentlighet</Label>
              </div>
              
              {thresholds.clearly_trivial_threshold?.derived_from === 'overall_materiality' ? (
                <div className="space-y-2">
                  <Label>Procent av väsentlighet</Label>
                  <div className="flex items-center gap-4">
                    <Slider
                      value={[trivialPercentage]}
                      onValueChange={([value]) => updateThreshold('clearly_trivial_threshold', { 
                        percentage: value,
                        value: Math.round(overallValue * (value / 100))
                      })}
                      min={1}
                      max={10}
                      step={1}
                      className="flex-1"
                    />
                    <span className="text-sm font-medium w-12 text-right">{trivialPercentage}%</span>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    = {formatCurrency(calculatedTrivial)} (vanligt: 5% av väsentlighet)
                  </p>
                </div>
              ) : (
                <div className="space-y-2">
                  <Label>Belopp (SEK)</Label>
                  <Input
                    type="number"
                    placeholder="100000"
                    value={thresholds.clearly_trivial_threshold?.value || ''}
                    onChange={(e) => updateThreshold('clearly_trivial_threshold', { 
                      value: parseInt(e.target.value) || 0 
                    })}
                    className="font-mono"
                  />
                </div>
              )}
            </AccordionContent>
          </AccordionItem>

          {/* Specific Materiality */}
          <AccordionItem value="specific" className="border rounded-lg px-4">
            <AccordionTrigger className="hover:no-underline">
              <div className="flex items-center gap-3">
                <AlertTriangle className="h-5 w-5 text-destructive" />
                <span className="font-medium">Specifik väsentlighet</span>
                {thresholds.specific_materiality?.qualitative && (
                  <Badge variant="destructive">Kvalitativ</Badge>
                )}
              </div>
            </AccordionTrigger>
            <AccordionContent className="pt-4 space-y-4">
              <div className="flex items-start gap-2 text-sm text-muted-foreground bg-muted/50 p-3 rounded-md">
                <Info className="h-4 w-4 mt-0.5 shrink-0" />
                <p>{THRESHOLD_DEFINITIONS.specific_materiality.description}</p>
              </div>
              
              <div className="flex items-center gap-4">
                <Switch
                  checked={thresholds.specific_materiality?.qualitative || false}
                  onCheckedChange={(checked) => updateThreshold('specific_materiality', { 
                    qualitative: checked,
                    value: checked ? 0 : thresholds.specific_materiality?.value || 0
                  })}
                />
                <Label className="cursor-pointer">Kvalitativ bedömning (ingen beloppsgräns)</Label>
              </div>
              
              {!thresholds.specific_materiality?.qualitative && (
                <div className="space-y-2">
                  <Label>Belopp (SEK)</Label>
                  <Input
                    type="number"
                    placeholder="0"
                    value={thresholds.specific_materiality?.value || ''}
                    onChange={(e) => updateThreshold('specific_materiality', { 
                      value: parseInt(e.target.value) || 0 
                    })}
                    className="font-mono"
                  />
                </div>
              )}
              
              <div className="space-y-3">
                <Label>Tillämpningsområden</Label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {SPECIFIC_AREAS.map(area => {
                    const isChecked = thresholds.specific_materiality?.applies_to?.includes(area.value) || false;
                    return (
                      <div key={area.value} className="flex items-center gap-2">
                        <Checkbox
                          id={area.value}
                          checked={isChecked}
                          onCheckedChange={(checked) => {
                            const current = thresholds.specific_materiality?.applies_to || [];
                            const updated = checked 
                              ? [...current, area.value]
                              : current.filter(a => a !== area.value);
                            updateThreshold('specific_materiality', { applies_to: updated });
                          }}
                        />
                        <Label htmlFor={area.value} className="cursor-pointer text-sm">
                          {area.label}
                        </Label>
                      </div>
                    );
                  })}
                </div>
              </div>
            </AccordionContent>
          </AccordionItem>
        </Accordion>

        {/* Summary */}
        {overallValue > 0 && (
          <div className="bg-primary/5 border border-primary/20 rounded-lg p-4 mt-4">
            <div className="flex items-center gap-2 mb-3">
              <Calculator className="h-4 w-4 text-primary" />
              <span className="font-medium text-sm">Sammanfattning av trösklar</span>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <div>
                <p className="text-muted-foreground">Väsentlighet</p>
                <p className="font-medium">{formatCurrency(overallValue)}</p>
              </div>
              <div>
                <p className="text-muted-foreground">Prestandaväsentlighet</p>
                <p className="font-medium">
                  {formatCurrency(thresholds.performance_materiality?.value || calculatedPerformance)}
                </p>
              </div>
              <div>
                <p className="text-muted-foreground">Trivialitetsgräns</p>
                <p className="font-medium">
                  {formatCurrency(thresholds.clearly_trivial_threshold?.value || calculatedTrivial)}
                </p>
              </div>
              <div>
                <p className="text-muted-foreground">Specifik</p>
                <p className="font-medium">
                  {thresholds.specific_materiality?.qualitative 
                    ? 'Kvalitativ' 
                    : formatCurrency(thresholds.specific_materiality?.value || 0)}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Save button */}
        {onSave && (
          <div className="flex justify-end pt-4 border-t">
            <Button onClick={onSave} disabled={isSaving}>
              <Save className="h-4 w-4 mr-2" />
              {isSaving ? 'Sparar...' : 'Spara trösklar'}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
